import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api, { apiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Cpu, HardDrives, Database, Users, StackSimple, Warning, CurrencyDollar, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

function Stat({ label, value, sub, Icon, warn }) {
  return (
    <div className={`rounded-2xl border p-6 ${warn ? "border-warning-foreground/40 bg-warning/30" : "border-border bg-card"}`}>
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{label}</p>
        {Icon && <Icon size={18} weight="duotone" className="text-muted-foreground" />}
      </div>
      <p className="mt-2 font-display text-2xl font-bold">{value}</p>
      {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

function Bar({ label, pct }) {
  const warn = pct > 85;
  return (
    <div>
      <div className="mb-1.5 flex justify-between text-sm"><span className="text-muted-foreground">{label}</span><span className="font-medium">{pct}%</span></div>
      <div className="h-2 overflow-hidden rounded-full bg-secondary">
        <div className={`h-full rounded-full ${warn ? "bg-warning-foreground" : "bg-primary"}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function Admin() {
  const [ov, setOv] = useState(null);
  const [apps, setApps] = useState([]);
  const [audit, setAudit] = useState([]);
  const [setup, setSetup] = useState(null);
  const [form, setForm] = useState({});
  const [mode, setMode] = useState("simulation");
  const [validation, setValidation] = useState(null);
  const [validating, setValidating] = useState(false);

  const loadOverview = () => api.get("/admin/overview").then((r) => { setOv(r.data); setMode(r.data.mode); }).catch(() => {});
  const loadSetup = () => api.get("/admin/setup").then((r) => setSetup(r.data)).catch(() => {});

  useEffect(() => {
    loadOverview();
    api.get("/admin/applications").then((r) => setApps(r.data)).catch(() => {});
    api.get("/admin/audit").then((r) => setAudit(r.data)).catch(() => {});
    loadSetup();
  }, []);

  const saveSetup = async () => {
    try {
      await api.post("/admin/setup", { ...form, mode });
      toast.success("Infrastructure settings saved.");
      setForm({});
      await loadSetup(); await loadOverview();
    } catch (e) { toast.error(apiError(e)); }
  };

  const validate = async () => {
    setValidating(true);
    try {
      const { data } = await api.post("/admin/setup/validate");
      setValidation(data);
      if (data.ready) toast.success("Everything looks connected!");
      else toast.info("Some infrastructure still needs connecting.");
    } catch (e) { toast.error(apiError(e)); } finally { setValidating(false); }
  };

  const enableProduction = async () => {
    try {
      await api.post("/admin/setup", { mode: "production" });
      setMode("production");
      toast.success("Production hosting enabled.");
      await loadOverview();
    } catch (e) { toast.error(apiError(e)); }
  };

  const field = (key, label, placeholder, type = "text") => (
    <div className="space-y-1.5">
      <Label htmlFor={key}>{label} {setup?.fields?.[key]?.set && <span className="ml-1 text-xs text-success-foreground">saved</span>}</Label>
      <Input id={key} type={type} placeholder={setup?.fields?.[key]?.value || placeholder}
        value={form[key] ?? ""} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
        data-testid={`setup-${key}`} className="rounded-xl" />
    </div>
  );

  return (
    <Layout>
      <div className="mx-auto max-w-6xl px-6 py-10 sm:px-10">
        <div className="flex items-center gap-3">
          <h1 className="font-display text-4xl font-bold tracking-tight">Admin</h1>
          <span className="rounded-full bg-secondary px-3 py-1 text-xs font-semibold capitalize" data-testid="admin-mode">{mode} mode</span>
        </div>
        <p className="mt-2 text-muted-foreground">Your infrastructure at a glance — no SSH, no provider dashboards.</p>

        <Tabs defaultValue="overview" className="mt-8">
          <TabsList className="rounded-full">
            <TabsTrigger value="overview" className="rounded-full" data-testid="admin-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="apps" className="rounded-full" data-testid="admin-tab-apps">Applications</TabsTrigger>
            <TabsTrigger value="setup" className="rounded-full" data-testid="admin-tab-setup">Setup Wizard</TabsTrigger>
            <TabsTrigger value="audit" className="rounded-full" data-testid="admin-tab-audit">Audit log</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6">
            {ov ? (
              <>
                {ov.capacity_warning && (
                  <div className="mb-4 flex items-center justify-between rounded-xl bg-warning/40 px-4 py-3 text-sm text-warning-foreground" data-testid="capacity-warning">
                    <span className="flex items-center gap-2"><Warning size={16} weight="fill" /> Hosting capacity is reaching its limit.</span>
                    <Button size="sm" className="rounded-full">Add capacity</Button>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                  <Stat label="Servers online" value={ov.servers_online} sub={ov.node?.name} Icon={StackSimple} />
                  <Stat label="Apps online" value={ov.applications_online} sub={`${ov.applications_stopped} stopped`} Icon={CheckCircle} />
                  <Stat label="Customers" value={ov.customers} Icon={Users} />
                  <Stat label="Failed deployments" value={ov.failed_deployments} warn={ov.failed_deployments > 0} Icon={Warning} />
                  <Stat label="Database health" value={ov.database_health?.ok ? "Healthy" : "Down"} sub={ov.database_health?.version} Icon={Database} warn={!ov.database_health?.ok} />
                  <Stat label="Backups" value={ov.backups} Icon={HardDrives} />
                  <Stat label="Build queue" value={ov.build_queue} Icon={StackSimple} />
                  <Stat label="Infra errors" value={ov.infrastructure_errors} warn={ov.infrastructure_errors > 0} Icon={Warning} />
                </div>

                <div className="mt-4 grid gap-4 lg:grid-cols-3">
                  <div className="rounded-2xl border border-border bg-card p-6 lg:col-span-2">
                    <p className="mb-4 text-sm font-semibold">{ov.node?.name} capacity (real)</p>
                    <div className="space-y-4">
                      <Bar label="CPU" pct={ov.node.cpu_pct} />
                      <Bar label="Memory" pct={ov.node.ram_pct} />
                      <Bar label="Disk" pct={ov.node.disk_pct} />
                    </div>
                    <div className="mt-4 grid grid-cols-3 gap-4 text-center text-xs text-muted-foreground">
                      <div><p className="font-display text-lg font-bold text-foreground">{ov.node.cores}</p>cores</div>
                      <div><p className="font-display text-lg font-bold text-foreground">{ov.node.ram_total_gb} GB</p>total RAM</div>
                      <div><p className="font-display text-lg font-bold text-foreground">{ov.node.disk_total_gb} GB</p>total disk</div>
                    </div>
                  </div>
                  <div className="rounded-2xl border border-border bg-card p-6">
                    <p className="mb-4 flex items-center gap-2 text-sm font-semibold"><CurrencyDollar size={16} weight="duotone" /> Economics</p>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between"><span className="text-muted-foreground">Revenue</span><span className="font-medium">${ov.monthly_revenue}/mo</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Infra cost</span><span className="font-medium">${ov.monthly_cost}/mo</span></div>
                      <div className="flex justify-between border-t border-border pt-3"><span className="text-muted-foreground">Gross margin</span><span className="font-display text-lg font-bold">${ov.gross_margin}/mo</span></div>
                    </div>
                  </div>
                </div>
              </>
            ) : <p className="text-muted-foreground">Loading…</p>}
          </TabsContent>

          <TabsContent value="apps" className="mt-6">
            <div className="overflow-hidden rounded-2xl border border-border bg-card">
              <table className="w-full text-sm">
                <thead className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <tr><th className="px-5 py-3">App</th><th className="px-5 py-3">Owner</th><th className="px-5 py-3">Status</th><th className="px-5 py-3">Plan</th><th className="px-5 py-3">Mode</th></tr>
                </thead>
                <tbody>
                  {apps.map((a) => (
                    <tr key={a.id} className="border-b border-border last:border-0" data-testid={`admin-app-${a.id}`}>
                      <td className="px-5 py-3 font-medium">{a.name}</td>
                      <td className="px-5 py-3 text-muted-foreground">{a.owner || "—"}</td>
                      <td className="px-5 py-3"><StatusBadge status={a.status} /></td>
                      <td className="px-5 py-3 capitalize text-muted-foreground">{a.plan || "—"}</td>
                      <td className="px-5 py-3 capitalize text-muted-foreground">{a.mode || "—"}</td>
                    </tr>
                  ))}
                  {apps.length === 0 && <tr><td colSpan={5} className="px-5 py-8 text-center text-muted-foreground">No applications yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="setup" className="mt-6">
            <div className="max-w-xl rounded-2xl border border-border bg-card p-8">
              <p className="font-display text-xl font-medium">Infrastructure Setup Wizard</p>
              <p className="mt-2 text-sm text-muted-foreground">Connect real infrastructure once. Credentials are encrypted and never shown again. Production mode never falls back to simulation — if infra isn't connected, deployments fail honestly.</p>

              <div className="mt-6 space-y-4">
                <div className="space-y-1.5">
                  <Label>Deployment mode</Label>
                  <Select value={mode} onValueChange={setMode}>
                    <SelectTrigger className="rounded-xl" data-testid="setup-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="simulation">Development / Simulation</SelectItem>
                      <SelectItem value="production">Production / Real infrastructure</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {field("domain", "Platform domain", "hostless.app")}
                {field("docker_host", "Container host", "tcp://host:2376")}
                {field("proxy_admin_url", "Reverse proxy admin API", "http://caddy:2019")}
                {field("dns_provider", "DNS provider", "cloudflare")}
                {field("dns_token", "DNS API token", "••••••••", "password")}
                {field("acme_email", "ACME / Let's Encrypt email", "you@company.com")}
              </div>
              <Button onClick={saveSetup} className="mt-6 rounded-full active:scale-95" data-testid="setup-save">Save settings</Button>
            </div>

            <div className="mt-4 max-w-xl rounded-2xl border border-border bg-card p-8">
              <div className="flex items-center justify-between">
                <p className="font-display text-xl font-medium">Connection status</p>
                <Button variant="outline" onClick={validate} disabled={validating} className="rounded-full" data-testid="setup-validate">
                  {validating ? "Checking…" : "Check connection"}
                </Button>
              </div>
              {validation ? (
                <ul className="mt-5 space-y-2">
                  {validation.checks.map((c) => (
                    <li key={c.key} className="flex items-center justify-between rounded-xl bg-secondary px-4 py-2.5 text-sm" data-testid={`check-${c.key}`}>
                      <span className="flex items-center gap-2">
                        {c.ok ? <CheckCircle size={16} weight="fill" className="text-success-foreground" /> : <Warning size={16} weight="fill" className="text-warning-foreground" />}
                        {c.label}
                      </span>
                      <span className="text-xs text-muted-foreground">{c.detail}</span>
                    </li>
                  ))}
                </ul>
              ) : <p className="mt-4 text-sm text-muted-foreground">Run a check to see what's connected before enabling production hosting.</p>}

              <Button onClick={enableProduction} disabled={!validation?.ready} className="mt-6 w-full rounded-full active:scale-95" data-testid="enable-production">
                {validation?.ready ? "Enable production hosting" : "Enable production hosting (connect everything first)"}
              </Button>
              <p className="mt-3 text-xs text-muted-foreground">Production never falls back to simulation. If infrastructure isn't connected, deployments fail honestly instead of pretending.</p>
            </div>
          </TabsContent>

          <TabsContent value="audit" className="mt-6">
            <div className="rounded-2xl border border-border bg-card p-2">
              <ul className="divide-y divide-border">
                {audit.map((a) => (
                  <li key={a.id} className="flex items-center justify-between px-4 py-3 text-sm">
                    <div>
                      <span className="font-medium">{a.action}</span>
                      <span className="ml-2 text-muted-foreground">{a.user_email}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-xs font-semibold ${a.result === "failed" ? "text-destructive" : "text-success-foreground"}`}>{a.result}</span>
                      <span className="text-xs text-muted-foreground">{new Date(a.created_at).toLocaleString()}</span>
                    </div>
                  </li>
                ))}
                {audit.length === 0 && <li className="px-4 py-8 text-center text-muted-foreground">No audit entries yet.</li>}
              </ul>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
